// Generated by tutorials/record.cjs from tutorials/articles/h30-nutrition-compliance.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h30 --text-only` (wording) or
// `node tutorials/record.cjs h30` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'nutrition-compliance',
  catalogId: 'h30',
  title: 'Nutrition Compliance',
  summary: 'Nutrition Compliance looks through your residents\' weights, diet orders and tube feeding records and lists the ones that need a look. Review each finding, record what you decided, and check weights that look like entry errors.',
  audience: 'Dietitians, MDS coordinators and dietary managers',
  area: 'clinical',
  learn: [
    'What the page checks, and what a finding is',
    'Read a finding\'s evidence and record what you decided',
    'See one resident\'s weights, diet and findings in one place',
    'Check weights that look like entry errors',
    'Sign off the rules for your facility, and print the reports',
  ],
  keywords: [
    'nutrition compliance',
    'pdpm',
    'findings',
    'review queue',
    'weight loss',
    'weight gain',
    'significant weight change',
    'weight trigger',
    'low bmi',
    'admission weight',
    'weight due',
    'tube feeding',
    'documentation readiness',
    'weight data',
    'entry error',
    'kg',
    'sign-off',
    'registered dietitian',
    'mds coordinator',
    'section k',
    'evidence packet',
    'reports',
  ],
  steps: [
    {
      id: 'open',
      part: 'Nutrition Compliance at a glance',
      heading: 'Open Nutrition Compliance',
      text: 'Under Clinical, select Nutrition Compliance. Search for pdpm finds it too: the page used to be called PDPM.\n\nEach time you open it, Dietech checks your residents\' records (weights, heights, diet orders and tube feeding orders) against a set of rules and lists what needs a look. The time of the last check is next to the title. Nothing on the page changes a resident\'s records: it points things out, and your staff decide.',
      media: {
        image: {
          src: '/help/nutrition-compliance/open.webp',
          width: 1280,
          height: 720,
        },
        alt: 'The Nutrition Compliance page at Maple Grove Care Center: the summary cards, the To check list and the Residents requiring attention list.',
      },
    },
    {
      id: 'cards',
      part: 'Nutrition Compliance at a glance',
      heading: 'The summary cards',
      text: 'Residents in scope is every active resident. Open findings counts what is waiting for review, split into critical, high and routine. Select a number to open the review queue with just those.\n\nMDS discrepancies says MDS data not connected until MDS assessments are entered for your facility. Documentation readiness is the share of residents whose records pass every check: a recent weight, a height, a current diet order and, for tube-fed residents, a tube feeding order.',
      media: {
        image: {
          src: '/help/nutrition-compliance/cards.webp',
          width: 1280,
          height: 120,
        },
        alt: 'The four cards: 32 residents in scope, 7 open findings (1 critical, 4 high, 2 routine), MDS data not connected, and documentation readiness 84.4%.',
      },
    },
    {
      id: 'to-check',
      part: 'Nutrition Compliance at a glance',
      heading: 'The To check list',
      text: 'To check lists what to set up or look at before you rely on the findings. Here, three weights on file look like entry errors, and 3 of the 12 rules are approved at this facility.\n\nEach line has a link that opens the tab where it is done. A line also appears when a consistency on a current diet order has no IDDSI level. When there is nothing to do, the list is not shown.',
      media: {
        image: {
          src: '/help/nutrition-compliance/to-check.webp',
          width: 1280,
          height: 124,
        },
        alt: 'The To check list: 3 weights on file look like entry errors (2 high confidence), with Check weights; 3 of 12 rules are approved at this facility, with Open sign-off.',
      },
    },
    {
      id: 'list',
      part: 'Work a finding',
      heading: 'Residents requiring attention',
      text: 'Each row is one finding for one resident: the finding and its type, how urgent it is, the evidence in short, who it is assigned to, how many days old it is and when it is due, and its status. Critical findings are listed first.\n\nType in the search box to find a resident or a kind of finding. Select a column heading to sort.',
      media: {
        image: {
          src: '/help/nutrition-compliance/list.webp',
          width: 1280,
          height: 604,
        },
        alt: 'The Residents requiring attention list: Whitfield, Eleanor with Significant weight loss (Critical, New) at the top, then Ortega, Luis, Vasquez, Irene and four more.',
      },
    },
    {
      id: 'what-is-a-finding',
      part: 'Work a finding',
      heading: 'What a finding means',
      text: 'A finding is a record that needs a look. It is not a diagnosis, and it does not say what to do clinically: it says what Dietech found, why, and what the rule suggests as a next step. Your staff decide.\n\nEach finding has a type. A Clinical indicator is a significant weight loss or gain, or a BMI below your facility\'s screening threshold. Documentation means something is missing, such as no weight within your facility\'s weight interval, no admission weight, no current diet order, or a resident marked tube fed with no tube feeding order. A Data check is a record that looks wrong, such as a weight that may have been entered in kilograms.\n\nYour facility\'s own weight triggers decide whether a weight change is significant: for example, 5% in 30 days. They are the same triggers the Dashboard and the Weight Trigger report use.\n\nWhen the records change so the finding no longer applies, such as a new weight that is back in line, the next check resolves it for you and says why in its history.',
      textOnly: true,
    },
    {
      id: 'review',
      part: 'Work a finding',
      heading: 'Open a finding',
      text: 'Select a row, or Review at its end. The finding opens on the right.\n\nWhat was detected says what Dietech found. Why names the rule, here your facility\'s 30-day weight trigger. Evidence lists the weights behind it, with their dates and where each came from. Missing information says what Dietech cannot know, such as whether the weight change was planned. Recommended next step is the rule\'s suggestion. History, at the bottom, lists everything done to the finding.',
      media: {
        image: {
          src: '/help/nutrition-compliance/review.webp',
          width: 952,
          height: 1266,
        },
        clip: {
          src: '/help/nutrition-compliance/review.mp4',
          width: 952,
          height: 1266,
          durationSec: 2,
        },
        alt: 'The Significant weight loss finding for Whitfield, Eleanor: Critical and New, what was detected (a 6.2% loss over 29 days), why, the evidence table with the current weight and the 30-day trigger, missing information and the recommended next step.',
      },
    },
    {
      id: 'decisions',
      part: 'Work a finding',
      heading: 'What you can record',
      text: 'The buttons at the bottom record what happens next. Each one goes into the finding\'s history with your name and the time.\n\nAssign gives the finding to someone, with a due date if you like. Start review shows that someone is working on it. Request information says what is needed and from whom, and sets the status to Awaiting information. Resolve says how it was dealt with. Dismiss says why it does not need action. Add note records anything else.\n\nResolve, Dismiss, Request information and Add note need words: Save stays off until you write them. A resolved or dismissed finding can be reopened, with a reason.',
      media: {
        image: {
          src: '/help/nutrition-compliance/decisions.webp',
          width: 952,
          height: 1266,
        },
        alt: 'The buttons along the bottom of the finding: Open resident, Assign, Start review, Request information, Resolve, Dismiss, Add note and Close.',
      },
    },
    {
      id: 'assign',
      part: 'Work a finding',
      heading: 'Assign the finding',
      text: 'Select Assign, pick the person in Assign to, and add a due date if you like. The list shows the people who can use this page. Select Assign to save.',
      media: {
        image: {
          src: '/help/nutrition-compliance/assign.webp',
          width: 708,
          height: 426,
        },
        clip: {
          src: '/help/nutrition-compliance/assign.mp4',
          width: 708,
          height: 426,
          durationSec: 4.3,
        },
        alt: 'The Assign finding window for Significant weight loss, Whitfield, Eleanor: Assign to Hannah Price, RD, and an empty Due date (optional).',
      },
    },
    {
      id: 'resolve',
      part: 'Work a finding',
      heading: 'Resolve it, saying how',
      text: 'When the finding has been dealt with, select Resolve and say how, in words the next reader can check.\n\nFor a weight loss or gain, check Planned change when the change is part of a documented physician-prescribed regimen. Dietech does not hold that information, so the reviewer records it here.',
      media: {
        image: {
          src: '/help/nutrition-compliance/resolve.webp',
          width: 708,
          height: 496,
        },
        clip: {
          src: '/help/nutrition-compliance/resolve.mp4',
          width: 708,
          height: 496,
          durationSec: 6,
        },
        alt: 'The Resolve finding window: the Planned change check box, and How was it resolved filled in with the dietitian\'s review and the care plan update.',
      },
    },
    {
      id: 'history',
      part: 'Work a finding',
      heading: 'The finding is resolved',
      text: 'Select Resolve. The finding now shows Resolved at the top, with your words under Status note. History, at the bottom, lists who assigned it and who resolved it, newest first.\n\nA resolved finding stays closed while the records tell the same story. It comes back, marked Reopened with the reason, only when something new happens, such as another of your weight triggers firing.',
      media: {
        image: {
          src: '/help/nutrition-compliance/history.webp',
          width: 952,
          height: 1266,
        },
        alt: 'The bottom of the resolved finding: the rule, and the history with Resolved by Dana Morgan and the reason, Assigned to Hannah Price, RD, and Raised by Dietech (evaluation). Reopen and Add note are now the buttons.',
      },
    },
    {
      id: 'resident',
      part: 'One resident',
      heading: 'Open the resident',
      text: 'Select Open resident, or a resident\'s name in any list. Their nutrition review opens on the right.\n\nSummary shows the current weight and recent trend, height, BMI, the active diet order, whether they are tube fed, allergies and the admission date. Documentation completeness shows each check and its result. Active findings lists what is still open for this resident.',
      media: {
        image: {
          src: '/help/nutrition-compliance/resident.webp',
          width: 1280,
          height: 1206,
        },
        clip: {
          src: '/help/nutrition-compliance/resident.mp4',
          width: 1280,
          height: 1206,
          durationSec: 2,
        },
        alt: 'The nutrition review for Whitfield, Eleanor on the Summary tab: current weight 128 lb, recent trend, height, BMI, active diet, tube fed No, and the documentation completeness checks.',
      },
    },
    {
      id: 'weights',
      part: 'One resident',
      heading: 'Weights',
      text: 'Weights shows the weight chart, then your facility\'s weight triggers and whether each one is met. Here the 30-day trigger shows a 6.2% loss.\n\nBelow that is a separate calculation for the MDS team, worked out the Section K way; the two can differ. Weight history lists every weight with its source. A weight the checks left out says Not used, and why: correct it in Weight Entry.',
      media: {
        image: {
          src: '/help/nutrition-compliance/weights.webp',
          width: 1280,
          height: 1206,
        },
        clip: {
          src: '/help/nutrition-compliance/weights.mp4',
          width: 1280,
          height: 1206,
          durationSec: 3.4,
        },
        alt: 'The Weights tab for Whitfield, Eleanor: the weight chart falling from about 138 lb to 128 lb, and the facility trigger table with the 30-day window showing Loss -6.2%.',
      },
    },
    {
      id: 'resident-history',
      part: 'One resident',
      heading: 'The other tabs, and the evidence packet',
      text: 'Diet & Orders lists the diet orders of the last 30 days and the tube feeding orders. Intake & Feeding shows what Dietech records about intake: intake studies only, and tube feeding as ordered, not as given. MDS Reconciliation compares MDS Section K answers entered for the resident with Dietech\'s records; Dietech never changes an MDS answer.\n\nHistory lists the resident\'s findings and everything done to them. Evidence packet (PDF) prints all of it for the resident.',
      media: {
        image: {
          src: '/help/nutrition-compliance/resident-history.webp',
          width: 1280,
          height: 1206,
        },
        clip: {
          src: '/help/nutrition-compliance/resident-history.mp4',
          width: 1280,
          height: 1206,
          durationSec: 3.6,
        },
        alt: 'The History tab for Whitfield, Eleanor: the Significant weight loss finding, Resolved, and the review and actions list with Resolved and Assigned by Dana Morgan.',
      },
    },
    {
      id: 'queue',
      part: 'The review queue',
      heading: 'Work through the review queue',
      text: 'The Review queue tab lists the same findings with more ways to narrow them. Pick a view: Open, Assigned to me, Awaiting information, Resolved or Dismissed.\n\nThen filter by Severity, Type, Finding, Unit, who it is Assigned to, or the dates it was raised. Clear filters puts them back. Select a row to open the finding, as before.',
      media: {
        image: {
          src: '/help/nutrition-compliance/queue.webp',
          width: 1280,
          height: 632,
        },
        clip: {
          src: '/help/nutrition-compliance/queue.mp4',
          width: 1280,
          height: 632,
          durationSec: 2.8,
        },
        alt: 'The Review queue on the Awaiting information view: Oyelaran, Samuel with Tube feeding flag without a tube feeding order, assigned to Dana Morgan.',
      },
    },
    {
      id: 'weight-data',
      part: 'Weights that look wrong',
      heading: 'Weight data',
      text: 'The Weight data tab lists weights on file that look like entry errors: probably entered in kilograms, converted twice, missing a decimal point, impossible, out of line with the weights before and after, or two different weights the same day. Why explains each one, next to the weights around it.\n\nConfidence says how likely the entry is wrong. Low-confidence items, such as two weights the same day, are hidden at first: only the last weight of a day is used anyway. Use Period, Show and Confidence to change what is listed.',
      media: {
        image: {
          src: '/help/nutrition-compliance/weight-data.webp',
          width: 1280,
          height: 632,
        },
        clip: {
          src: '/help/nutrition-compliance/weight-data.mp4',
          width: 1280,
          height: 632,
          durationSec: 2,
        },
        alt: 'The Weight data tab: Holloway, Beatrice 49.4 lb Possibly entered in kg (High), Castillo, Vernon 1562 lb Possibly missing a decimal point (High), and Adeyemi, Ruth 113 lb Out of line (Medium).',
      },
    },
    {
      id: 'weight-review',
      part: 'Weights that look wrong',
      heading: 'Record a decision about a weight',
      text: 'Check the weight against the chart, then select Review on its row. Pick a Decision: Entry error leaves the weight out of every Nutrition Compliance calculation; Correct as recorded tells the checks to use it. Give the reason and select Record decision.\n\nThe decision does not change the weight. To fix the weight itself, edit it in Weight Entry; the decision then stops applying. Recording decisions needs level 3.',
      media: {
        image: {
          src: '/help/nutrition-compliance/weight-review.webp',
          width: 942,
          height: 614,
        },
        clip: {
          src: '/help/nutrition-compliance/weight-review.mp4',
          width: 942,
          height: 614,
          durationSec: 6,
        },
        alt: 'The review window for 49.4 lb on HOLLOWAY, BEATRICE: possibly entered in kg, the weights around it, Decision Entry error: leave it out of calculations, and a reason.',
      },
    },
    {
      id: 'signoff',
      part: 'Clinical sign-off',
      heading: 'Sign off the rules for your facility',
      text: 'Before you rely on the findings, your registered dietitian, and for MDS items your MDS coordinator, review each rule and the methods behind it on the Sign-off tab. Each row says what is approved, whose approval it needs, and its status here.\n\nA rule is approved only when everyone it needs has approved its current version. When a rule changes, it needs approving again. The Rules tab explains each rule: what it checks, where the definition comes from and how far back it looks.',
      media: {
        image: {
          src: '/help/nutrition-compliance/signoff.webp',
          width: 1280,
          height: 632,
        },
        clip: {
          src: '/help/nutrition-compliance/signoff.mp4',
          width: 1280,
          height: 632,
          durationSec: 2,
        },
        alt: 'The Sign-off tab: 4 of 19 approved at this facility; Significant weight loss, Significant weight gain and Weight not recorded within facility policy, each needing the registered dietitian, approved at this facility, with Record sign-off on each row.',
      },
    },
    {
      id: 'record-signoff',
      part: 'Clinical sign-off',
      heading: 'Record a sign-off',
      text: 'Select Record sign-off on the row. Enter the clinician\'s name and credentials, their role, their decision and the date they reviewed it. Changes requested and Not approved need a comment saying what should change.\n\nSelect Record sign-off. You are recorded as the person who entered it; the clinician named is the one who decided. Recording sign-offs needs level 3. Show the sign-off record lists every decision ever recorded.',
      media: {
        image: {
          src: '/help/nutrition-compliance/record-signoff.webp',
          width: 942,
          height: 814,
        },
        clip: {
          src: '/help/nutrition-compliance/record-signoff.mp4',
          width: 942,
          height: 814,
          durationSec: 6,
        },
        alt: 'The sign-off window for Admission weight not recorded (facility policy), version 1: Hannah Price, RD, Registered dietitian, Approved, and the review date.',
      },
    },
    {
      id: 'reports',
      part: 'Reports',
      heading: 'Print the reports',
      text: 'Select the printer button at the top of the page. Pick a report: Open Nutrition Findings, Significant Weight Changes, Tube Feeding Documentation, MDS Section K Reconciliation or Documentation Readiness. Some ask for a date range. Pick PDF or Excel and select Download.\n\nEach report says what it cannot show, such as rules not yet approved at your facility. Reports also need access to Reports.',
      media: {
        image: {
          src: '/help/nutrition-compliance/reports.webp',
          width: 942,
          height: 526,
        },
        clip: {
          src: '/help/nutrition-compliance/reports.mp4',
          width: 942,
          height: 526,
          durationSec: 4.2,
        },
        alt: 'The Nutrition Compliance reports window with the Report list open: Open Nutrition Findings, Significant Weight Changes, Tube Feeding Documentation, MDS Section K Reconciliation and Documentation Readiness.',
      },
    },
  ],
  tips: [
    'Start each day with the critical and high findings: select the number on the Open findings card to see only those.',
    'Assign a finding to the person who will act on it, with a due date. They see it under Assigned to me in the review queue.',
    'Write a reason that the next reader can check, such as "re-weighed 10/30, 129 lb, RD aware". The reason stays in the finding\'s history.',
    'When a weight finding is caused by a wrong entry, fix the weight in Weight Entry. The finding clears on the next check.',
  ],
  notes: [
    'This page used to be called PDPM. It points out records that need a look; your staff decide what to do.',
    'What counts as a significant weight change is set by your facility\'s own weight triggers: Facility Settings, Assessment, Weight Entry tab, under Triggers. The Dashboard, the Weight Trigger report and this page all use the same triggers.',
    'The weight interval, the admission weight period, the nutrition assessment interval and the low BMI threshold are your facility\'s policies, set in Facility Settings under Nutrition Compliance. A low BMI is a risk indicator for the dietitian, not a diagnosis.',
    'Nothing on this page changes a weight, a diet order or an MDS answer. Correct a record where it is kept, such as Weight Entry or the resident\'s diet order.',
    'Access has three levels: 1 to view, 2 to work findings, and 3 to record weight decisions, consistency reviews and sign-offs. Reports also need access to Reports. Ask your administrator if a button is missing.',
    'The bell at the top of the page tells you when a new finding is raised or one is assigned to you. Selecting it opens the finding.',
  ],
  related: [
    'entering-weights',
    'running-a-report',
    'finding-a-resident',
    'settings-and-preferences',
    'glossary',
  ],
  recorded: '2026-11-03',
};

export default article;
